import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ImageUploader } from "@/components/v2/ImageUploader";
import { useToast } from "@/components/v2/Toast";
import {
  useTenantMe,
  useUpdateTenantMe,
  useUploadTenantLogo,
} from "@/lib/tenant-me";
import { CURRENCIES } from "@/lib/currency";
import { TIMEZONES } from "@/lib/timezones";
import { useRegisterStep } from "./OnboardingShell";
import { StepShell, StepBody } from "./_StepShell";
import { StepHero } from "./_StepHero";
import { StepFooter } from "./_StepFooter";

/**
 * Step 2 — Workspace setup. Name + currency + timezone are required;
 * logo + primary color are optional branding polish that takes effect
 * on the public booking page.
 *
 * The form groups Identity / Region / Brand into named sections so the
 * configuration is scannable instead of a single dense column.
 */
export function WorkspaceStep() {
  const me = useTenantMe();
  const update = useUpdateTenantMe();
  const uploadLogo = useUploadTenantLogo();
  const toast = useToast();

  const [name, setName] = useState("");
  const [primaryColor, setPrimaryColor] = useState("#1E3A5F");
  const [timezone, setTimezone] = useState("Asia/Manila");
  const [currency, setCurrency] = useState("PHP");

  useEffect(() => {
    const t = me.data?.tenant;
    if (t) {
      setName(t.name ?? "");
      setPrimaryColor(t.primaryColor ?? "#1E3A5F");
      setTimezone(t.timezone ?? "Asia/Manila");
      setCurrency(t.currency ?? "PHP");
    }
  }, [me.data]);

  const trimmedName = name.trim();
  const validCurrency = currency.trim().length === 3;
  const disabled = trimmedName.length === 0 || !validCurrency;

  // The booking-page slug is fixed at signup (name-slug + a random suffix, e.g.
  // `acme-studio-k7m3xq`) and never changes when the name is edited — so show
  // the tenant's REAL slug, not a name-derived preview.
  const bookingSlug = me.data?.tenant?.slug ?? null;

  useRegisterStep({
    onContinue: async () => {
      try {
        await update.mutateAsync({
          name: trimmedName,
          primaryColor: primaryColor.trim() || undefined,
          timezone,
          currency: currency.toUpperCase(),
        });
      } catch (e) {
        toast.error("Couldn't save workspace", (e as Error).message);
        throw e;
      }
    },
    disabled,
  });

  return (
    <StepShell variant="form">
      <StepHero
        eyebrow="Step 2 of 5"
        title="Set up your workspace"
        subtitle="A few details so customers know you and your bookings get the right currency."
      />

      <StepBody>
        <Section title="Identity">
          <div className="space-y-1.5">
            <Label htmlFor="ws-name" className="text-[13px] font-medium">
              Workspace name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="ws-name"
              placeholder="Acme Studio"
              autoFocus
              autoComplete="organization"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="h-11"
            />
            <p className="text-[12px] text-muted-foreground">
              {bookingSlug ? (
                <>
                  Your booking page is at{" "}
                  <span className="font-mono text-foreground">
                    /{bookingSlug}
                  </span>
                </>
              ) : (
                "Used on every booking confirmation and your public booking page."
              )}
            </p>
          </div>
        </Section>

        <Section title="Region">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="ws-currency" className="text-[13px] font-medium">
                Currency <span className="text-destructive">*</span>
              </Label>
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger id="ws-currency" className="h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map((c) => (
                    <SelectItem key={c.iso} value={c.iso}>
                      <span className="inline-flex items-center gap-2">
                        <span className="font-semibold w-5 text-center">
                          {c.symbol}
                        </span>
                        <span>{c.name}</span>
                        <span className="text-muted-foreground text-xs">
                          ({c.iso})
                        </span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ws-timezone" className="text-[13px] font-medium">
                Timezone
              </Label>
              <Select value={timezone} onValueChange={setTimezone}>
                <SelectTrigger id="ws-timezone" className="h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIMEZONES.map((tz) => (
                    <SelectItem key={tz} value={tz}>
                      {tz}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </Section>

        <Section title="Brand">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label className="text-[13px] font-medium">Logo</Label>
              <ImageUploader
                shape="square"
                size={80}
                label="Workspace logo"
                value={me.data?.tenant?.logoUrl}
                onUpload={(file) => uploadLogo.mutateAsync(file)}
                helpText="Optional. PNG/JPG, up to 5 MB."
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ws-color" className="text-[13px] font-medium">
                Primary color
              </Label>
              <div className="flex items-center gap-3">
                <Input
                  id="ws-color"
                  type="color"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  className="h-11 w-16 p-1 cursor-pointer"
                />
                <Input
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  className="h-11 font-mono text-xs"
                />
              </div>
              <p className="text-[12px] text-muted-foreground">
                Tints buttons + the brand bar on your booking page.
              </p>
            </div>
          </div>
        </Section>
      </StepBody>

      <StepFooter />
    </StepShell>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-4">
      <p className="text-[12px] font-semibold tracking-wide uppercase text-muted-foreground">
        {title}
      </p>
      <div className="space-y-4">{children}</div>
    </div>
  );
}
